import React, { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, Download, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { clientesService } from '@/services/clientes.service';
import { toast } from '@/stores/toast-store';

interface ImportCustomersModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function ImportCustomersModal({ onClose, onSuccess }: ImportCustomersModalProps) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{ success: number; errors: string[]; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Nombre': 'Abarrotes El Güero',
        'Teléfono': '5512345678',
        'Correo': 'guero@ejemplo.com',
        'RFC': 'XAXX010101000',
        'Dirección': 'Av. Central #123, Col. Centro',
        'Límite de Crédito': 5000.00
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
    
    ws['!cols'] = [
      {wch: 25}, {wch: 15}, {wch: 22}, {wch: 16}, {wch: 30}, {wch: 18}
    ];

    XLSX.writeFile(wb, 'Plantilla_Clientes.xlsx');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setResults(null);
    setProgress(10);

    try {
      const data = await file.arrayBuffer();
      setProgress(30);
      
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (!jsonData || jsonData.length === 0) {
        throw new Error('El archivo está vacío o no tiene el formato correcto');
      }

      setProgress(50);
      const response = await clientesService.bulkCreate(jsonData);
      
      setProgress(100);
      setResults({
        success: response.successCount || 0,
        errors: response.errors || [],
        total: response.total || jsonData.length
      });

      if (response.successCount > 0) {
        toast.success(`Se importaron ${response.successCount} clientes correctamente.`);
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al procesar el archivo Excel.');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[20px] shadow-ios-lg w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-ios-gray-5 flex items-center justify-between bg-ios-gray-6/30">
          <h2 className="text-xl font-semibold">Importar Clientes</h2>
          <button onClick={onClose} disabled={loading} className="p-2 text-ios-gray hover:bg-ios-gray-5 rounded-full transition-colors disabled:opacity-50">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!results ? (
            <>
              <div className="bg-ios-blue/5 border border-ios-blue/20 rounded-xl p-4 flex items-start gap-3">
                <FileSpreadsheet className="text-ios-blue shrink-0 mt-0.5" size={24} />
                <div className="text-sm">
                  <p className="font-medium text-ios-blue mb-1">Instrucciones</p>
                  <ul className="list-disc pl-4 space-y-1 text-ios-gray">
                    <li>Descarga la plantilla oficial y completa tus datos de clientes.</li>
                    <li>No modifiques la primera fila con los nombres de las columnas.</li>
                    <li>El nombre del cliente es obligatorio.</li>
                  </ul>
                  <button 
                    onClick={handleDownloadTemplate}
                    disabled={loading}
                    className="mt-3 text-ios-blue font-medium hover:underline flex items-center gap-1"
                  >
                    <Download size={16} /> Descargar Plantilla_Clientes.xlsx
                  </button>
                </div>
              </div>

              <div className="border-2 border-dashed border-ios-gray-4 rounded-xl p-8 text-center bg-ios-gray-6/30 transition-colors hover:bg-ios-gray-6/50">
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  disabled={loading}
                />
                
                {loading ? (
                  <div className="flex flex-col items-center justify-center">
                    <Loader2 className="animate-spin text-ios-blue mb-3" size={32} />
                    <p className="font-medium text-ios-gray">Procesando clientes...</p>
                    <div className="w-full max-w-xs bg-ios-gray-5 rounded-full h-2 mt-4 overflow-hidden">
                      <div className="bg-ios-blue h-full transition-all duration-300" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-ios-gray mb-3" size={32} />
                    <h3 className="font-medium mb-1">Selecciona tu archivo de Clientes</h3>
                    <p className="text-sm text-ios-gray mb-4">Soporta .xlsx y .xls</p>
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      className="ios-button w-full justify-center"
                    >
                      Buscar archivo
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className={`p-4 rounded-xl flex items-center justify-center flex-col text-center ${results.success > 0 ? 'bg-ios-green/10 text-ios-green' : 'bg-red-50 text-ios-red'}`}>
                <div className="text-4xl font-bold mb-1">{results.success} / {results.total}</div>
                <div className="font-medium">Clientes procesados con éxito</div>
              </div>

              {results.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-red-600 flex items-center gap-2 mb-2">
                    <X size={16} /> Detalle de errores:
                  </h4>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-100 max-h-48 overflow-y-auto">
                    <ul className="text-sm text-red-700 space-y-1 list-disc pl-4">
                      {results.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <button onClick={onClose} className="ios-button w-full justify-center mt-4 bg-ios-gray-5 text-black hover:bg-ios-gray-4">
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
